import React from 'react';
import { useDashboard } from '../api/hooks';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Clock, Package, Box } from 'lucide-react';

function Dashboard() {
  const { data = {}, isLoading } = useDashboard();
  const navigate = useNavigate();
  
  // Перевіряємо чи розрахунки зроблені
  const forecastsCalculated = localStorage.getItem('forecastsCalculated') === 'true';

  const metrics = data.dashboardMetrics || {};
  const alerts = data.alerts || [];
  const topProducts = data.topProducts || [];

  const criticalAlerts = alerts.filter(a => a.type === 'critical');
  const warningAlerts = alerts.filter(a => a.type === 'warning');
  const deadstockAlerts = alerts.filter(a => a.type === 'deadstock');

  const topChartData = {
    labels: topProducts.map((_, idx) => `#${idx + 1}`),
    datasets: [
      {
        label: 'шт',
        data: topProducts.map(p => p.sales),
        backgroundColor: '#3b82f6'
      }
    ]
  };

  const getPercentage = (count) => {
    const total = metrics.totalProducts || 1;
    return ((count / total) * 100).toFixed(1);
  };
  
  // Якщо розрахунки не зроблені, показуємо повідомлення
  if (!forecastsCalculated) {
    return (
      <div className="p-6 flex items-center justify-center h-[80vh]">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">📊</div>
          <h2 className="text-2xl font-bold mb-4">Дашборд недоступний</h2>
          <p className="text-gray-600 mb-6">
            Для відображення дашборду спочатку необхідно завантажити всі файли та провести розрахунки прогнозів.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
            <p className="font-semibold mb-2">Кроки:</p>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
              <li>Завантажте файл номенклатури</li>
              <li>Завантажте файл продажів</li>
              <li>Завантажте файл залишків</li>
              <li>Натисніть "Провести розрахунки" в меню зліва</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6 text-[85%] space-y-6">
      <h2 className="text-xl font-bold mb-2">Головна сторінка</h2>

      <div className="grid grid-cols-5 gap-4">
        <div className="bg-red-50 text-red-800 p-4 rounded shadow-sm">
          <div className="flex items-center gap-1 text-xs"><AlertTriangle size={14} /> Критичні позиції</div>
          <div className="text-2xl font-bold">{metrics.criticalItems ?? 0}</div>
          <div className="text-xs">{getPercentage(metrics.criticalItems)}% від загальної кількості</div>
        </div>
        <div className="bg-yellow-50 text-yellow-800 p-4 rounded shadow-sm">
          <div className="flex items-center gap-1 text-xs"><Clock size={14} /> Ризикові позиції</div>
          <div className="text-2xl font-bold">{metrics.riskItems ?? 0}</div>
          <div className="text-xs">{getPercentage(metrics.riskItems)}% від загальної кількості</div>
        </div>
        <div className="bg-blue-50 text-blue-800 p-4 rounded shadow-sm">
          <div className="flex items-center gap-1 text-xs"><Package size={14} /> Запаси без руху</div>
          <div className="text-2xl font-bold">{metrics.deadStock ?? 0}</div>
          <div className="text-xs">{getPercentage(metrics.deadStock)}% від загальної кількості</div>
        </div>
        <div className="col-span-2 bg-gray-50 text-gray-700 p-4 rounded shadow-sm">
          <div className="flex items-center gap-1 text-xs"><Box size={14} /> Всього товарів на складі</div>
          <div className="text-2xl font-bold">{metrics.totalProducts ?? 0}</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-red-50 p-4 rounded shadow">
          <h3 className="text-lg font-semibold text-red-700 mb-2">Купіть сьогодні!</h3>
          {(criticalAlerts.length === 0 ? <p className="text-sm text-gray-500">Немає критичних залишків</p> :
            <>
              {criticalAlerts.slice(0, 5).map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white p-3 mb-2 border-l-4 border-red-600 cursor-pointer hover:bg-red-100"
                  onClick={() => navigate('/leftovers')}
                >
                  <div className="font-semibold">{item.productName}</div>
                  <div className="text-xs text-gray-600">{item.category}</div>
                  <div className="text-xs text-gray-600">{item.storehouseName}</div>
                  <div className="text-sm mt-1 text-red-600">Купіть {Math.abs(item["+2 Weeks"])} штук!</div>
                </div>
              ))}
              <p
                onClick={() => navigate('/leftovers')}
                className="text-sm text-blue-600 hover:underline cursor-pointer"
              >Переглянути всі ({criticalAlerts.length})</p>
            </>)}
        </div>

        <div className="bg-yellow-50 p-4 rounded shadow">
          <h3 className="text-lg font-semibold text-yellow-700 mb-2">Зверніть увагу</h3>
          {(warningAlerts.length === 0 ? <p className="text-sm text-gray-500">Немає активних повідомлень</p> :
            <>
              {warningAlerts.slice(0, 5).map((item, idx) => (
                <div key={idx} className="bg-white p-3 mb-2 border-l-4 border-yellow-600">
                  <div className="font-semibold">{item.productName}</div>
                  <div className="text-xs text-gray-600">{item.category}</div>
                  <div className="text-xs text-gray-600">{item.storehouseName}</div>
                </div>
              ))}
              <p
                className="text-sm text-blue-600 hover:underline cursor-pointer"
                onClick={() => navigate('/leftovers')}
              >Переглянути всі ({warningAlerts.length})</p>
            </>)}
        </div>

        <div className="bg-blue-50 p-4 rounded shadow">
          <h3 className="text-lg font-semibold text-blue-700 mb-2">Запаси без руху</h3>
          {(deadstockAlerts.length === 0 ? <p className="text-sm text-gray-500">Немає мертвих запасів</p> :
            <>
              {deadstockAlerts.slice(0, 5).map((item, idx) => (
                <div key={idx} className="bg-white p-3 mb-2 border-l-4 border-blue-600">
                  <div className="font-semibold">{item.productName}</div>
                  <div className="text-xs text-gray-600">
                    Остання активність: {item.lastOrder ? item.lastOrder : 'Відсутні дані'}
                  </div>
                  <div className="text-xs text-gray-600">{item.storehouseName}</div>
                </div>
              ))}
              <p
                className="text-sm text-blue-600 hover:underline cursor-pointer"
                onClick={() => navigate('/leftovers')}
              >Переглянути всі ({deadstockAlerts.length})</p>
            </>)}
        </div>
      </div>

      <div className="bg-white p-6 rounded shadow w-3/4 mx-auto">
        <h3 className="text-lg font-semibold mb-4">ТОП-5 продажів</h3>
        <div className="h-48">
          <Bar
            data={topChartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  callbacks: {
                    label: ctx => `${ctx.parsed.y.toLocaleString()} шт`
                  }
                }
              },
              scales: {
                y: {
                  ticks: {
                    callback: value => `${value.toLocaleString()} шт`
                  }
                },
                x: {
                  ticks: {
                    display: true
                  }
                }
              }
            }}
          />
        </div>
        <ul className="mt-6 space-y-2 text-[15px]">
          {topProducts.map((item, idx) => (
            <li
              key={idx}
              className="flex items-start gap-2 leading-snug"
            >
              <span className="text-blue-700 min-w-[20px]">{idx + 1}.</span>
              <div className="flex-1">
                <div className="text-gray-800 ">{item.name} - {item.sales.toLocaleString()} шт</div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default Dashboard;
